<template>
  <WithAsideCards
    :data="data?.fields?.asideCards"
    data-article-listing-v="3.2.0"
  >
    <template #header>
      <BaseHeading size="h2" as="h3">{{ data?.fields?.header }}</BaseHeading>
    </template>
    <div :class="$style.contentContainer">
      <div v-if="data?.fields?.subheader" :class="$style.headerContainer">
        <em :class="$style.subheader">{{ data?.fields?.subheader }}</em>
      </div>

      <MarkedParser :md="data?.fields?.copy" :class="$style.copy" />

      <div v-if="showFilters" :class="$style.filters">
        <SearchField
          :class="$style.search"
          @request-query="handleSearch"
          @clear-query="handleSearch"
        />
        <ClientOnly>
          <BaseSelect
            v-if="categoryFilterOptions.length"
            v-model="selectedCategory"
            :class="$style.categories"
            :options="categoryFilterOptions"
          />
        </ClientOnly>
      </div>

      <div
        v-if="articlesStatus === 'idle' || articlesStatus === 'pending'"
        :class="$style.loadingContainer"
      >
        <LoaderDots :class="$style.loaderDots" />
      </div>

      <div
        v-else-if="articlesStatus === 'success' && pagedArticles.length > 0"
        :class="$style.cards"
      >
        <ArticleItem
          v-for="a in computedArticles"
          :key="a?.sys?.id"
          :data="a"
          :class="$style.card"
          :data-contentful-id="a?.sys?.id"
        />
      </div>

      <div
        v-else-if="
          articlesStatus === 'success' && computedArticles.length === 0
        "
      >
        {{ localizedStrings['no-results'] || 'No Results' }}
      </div>

      <CtaWings
        v-if="
          articlesStatus === 'success' &&
          computedArticles.length < allArticles.length
        "
      >
        <CallToAction :cta="data?.fields?.loadMoreCta" @click="onRequestMore" />
      </CtaWings>
    </div>
  </WithAsideCards>
</template>

<script setup lang="ts">
import type { TypeArticleListingWithoutUnresolvableLinksResponse } from '~/types/contentful'

const { localizedStrings } = useLocalizedStrings()

const PAGE_SIZE = 6

const { data = undefined, showFilters = true } = defineProps<{
  data?: TypeArticleListingWithoutUnresolvableLinksResponse
  showFilters?: boolean
}>()

// Search
const searchQuery = ref('')
const handleSearch = (val: string) => (searchQuery.value = val)

// Categories
const selectedCategory = ref('')
const categoryFilterOptions = buildCategoryOptions()

const {
  // statuses
  articlesStatus,

  // data
  allArticles,
  pagedArticles,

  // controls
  currentPage,
  loadMore,
} = useArticles({
  searchQuery,
  selectedCategory,
  pageSize: PAGE_SIZE,
  keyScope: computed(() => data?.sys?.id || 'article-listing'),
  clientOnly: true,
})

const computedArticles = computed(() => pagedArticles.value || [])

const onRequestMore = () => loadMore()

// Abstraction
function buildCategoryOptions() {
  const categories = data?.fields?.categories?.filter(instanceOfTag)
  const defaultOption = {
    label: data?.fields?.filterDefault || '',
    value: '',
  }
  return categories
    ? [
        defaultOption,
        ...categories.map((category) => ({
          label: category?.fields?.label || '',
          value: category?.fields?.value || '',
        })),
      ]
    : [defaultOption]
}

watch([searchQuery, selectedCategory], () => {
  currentPage.value = 1
})
</script>

<style lang="scss" module>
.contentContainer {
  color: var(--gray-8);
  text-align: center;

  @include media('sm') {
    text-align: left;
  }
}

.headerContainer {
  max-width: 544px;
  margin: 0 auto 22px;

  @include media('sm') {
    margin-left: 0;
  }
}

.header {
  @include fluid-type(32px, 52px);
  font-family: var(--roboto-condensed);
  line-height: math.div(54, 52);
  color: var(--black);
  margin-bottom: 19px;
}

.subheader {
  @include fluid-type(16px, 22px);
  font-family: var(--georgia);
  line-height: math.div(32, 22);
}

.copy {
  @include fluid-type(16px, 19px);
  line-height: math.div(30, 19);
  margin-bottom: 20px;
}

.cards {
  display: grid;
  grid-template-columns: 1fr;
  gap: 17px;
}

.cta {
  margin-top: 40px;
}

.cardContainer {
  display: grid;
  gap: 19px;
}

.filters {
  display: flex;
  flex-direction: column;
  margin-bottom: 17px;
  gap: 9px;

  @include media('md') {
    flex-direction: row;
    gap: 18px;
  }
}

.search {
  min-width: 250px;
}

.categories {
  max-width: 250px;
  width: 100%;
}

.loadingContainer {
  display: grid;
  place-items: center;
  min-height: 200px;
}

.loaderDots {
  width: max-content;
  margin: auto;
  display: block;
}
</style>
